import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  OrgMember,
  OrgMemberRole,
  OrgMemberStatus,
  OrgMembersState,
  RevokedInvite
} from './types';

const initialState: OrgMembersState = {
  members: [],
  loading: false,
  inviting: false,
  removing: false,
  updatingStatus: false,
  error: null,
  inviteError: null,
  inviteSuccess: false,
  removeError: null,
  removeSuccess: false,
  statusUpdateError: null,
  roleChangeError: null,
  roleChangeSuccess: false,
  revoking: false,
  revokeError: null,
  revokeSuccess: false,
  revokedInvites: [],
  revokedLoading: false,
  revokedError: null
};

export const slice = createSlice({
  name: 'orgMembers',
  initialState,
  reducers: {
    startLoading(state) {
      state.loading = true;
      state.error = null;
    },
    hasError(state, action: PayloadAction<string>) {
      state.error = action.payload;
      state.loading = false;
    },
    getMembersSuccess(state, action: PayloadAction<OrgMember[]>) {
      state.members = action.payload;
      state.loading = false;
    },
    startInviting(state) {
      state.inviting = true;
      state.inviteError = null;
      state.inviteSuccess = false;
    },
    inviteError(state, action: PayloadAction<string>) {
      state.inviting = false;
      state.inviteError = action.payload;
    },
    inviteSuccess(state) {
      state.inviting = false;
      state.inviteSuccess = true;
    },
    clearInviteStatus(state) {
      state.inviteError = null;
      state.inviteSuccess = false;
    },
    startRemoving(state) {
      state.removing = true;
      state.removeError = null;
      state.removeSuccess = false;
    },
    removeMemberSuccess(state, action: PayloadAction<string>) {
      state.removing = false;
      state.removeSuccess = true;
      state.members = state.members.filter((m) => m.userGuid !== action.payload);
    },
    removeError(state, action: PayloadAction<string>) {
      state.removing = false;
      state.removeError = action.payload;
    },
    clearRemoveStatus(state) {
      state.removeError = null;
      state.removeSuccess = false;
    },
    updateMemberStatusOptimistic(
      state,
      action: PayloadAction<{ userGuid: string; status: OrgMemberStatus }>
    ) {
      const member = state.members.find((m) => m.userGuid === action.payload.userGuid);
      if (member) member.status = action.payload.status;
      state.statusUpdateError = null;
    },
    revertMemberStatus(
      state,
      action: PayloadAction<{ userGuid: string; status: OrgMemberStatus }>
    ) {
      const member = state.members.find((m) => m.userGuid === action.payload.userGuid);
      if (member) member.status = action.payload.status;
    },
    statusUpdateError(state, action: PayloadAction<string>) {
      state.updatingStatus = false;
      state.statusUpdateError = action.payload;
    },
    updateMemberRoleOptimistic(
      state,
      action: PayloadAction<{ userGuid: string; role: OrgMemberRole }>
    ) {
      const member = state.members.find((m) => m.userGuid === action.payload.userGuid);
      if (member) member.role = action.payload.role;
      state.roleChangeError = null;
      state.roleChangeSuccess = false;
    },
    revertMemberRole(state, action: PayloadAction<{ userGuid: string; role: OrgMemberRole }>) {
      const member = state.members.find((m) => m.userGuid === action.payload.userGuid);
      if (member) member.role = action.payload.role;
    },
    roleChangeError(state, action: PayloadAction<string>) {
      state.roleChangeError = action.payload;
    },
    roleChangeSuccess(state) {
      state.roleChangeSuccess = true;
    },
    clearRoleChangeStatus(state) {
      state.roleChangeError = null;
      state.roleChangeSuccess = false;
    },
    startRevoking(state) {
      state.revoking = true;
      state.revokeError = null;
      state.revokeSuccess = false;
    },
    revokeSuccess(state, action: PayloadAction<string>) {
      state.revoking = false;
      state.revokeSuccess = true;
      state.members = state.members.filter((m) => m.memberGuid !== action.payload);
    },
    revokeError(state, action: PayloadAction<string>) {
      state.revoking = false;
      state.revokeError = action.payload;
    },
    clearRevokeStatus(state) {
      state.revokeError = null;
      state.revokeSuccess = false;
    },
    startRevokedLoading(state) {
      state.revokedLoading = true;
      state.revokedError = null;
    },
    getRevokedSuccess(state, action: PayloadAction<RevokedInvite[]>) {
      state.revokedLoading = false;
      state.revokedInvites = action.payload;
    },
    getRevokedError(state, action: PayloadAction<string>) {
      state.revokedLoading = false;
      state.revokedError = action.payload;
    }
  }
});

export default slice.reducer;
